import type { DataEditorRef, Item } from "@artis/data-grid";
import { type RefObject, useCallback, useEffect, useRef, useState } from "react";

import { sheetNamesEqual } from "@package/scratchpad-utils";
import { extractFormulaRefs } from "../formula/highlight";
import { anchorFor, applyPick, formatRef, hitCorner, pickSpanAt, rangeRect } from "../formula/picking";
import type { EditSession } from "./useEditSession";

const CORNER_TOLERANCE = 8;

type PickModeDeps = {
  gridRef: RefObject<DataEditorRef | null>;
  activeSheetNameRef: RefObject<string>;
};

export function usePickMode(session: EditSession, { gridRef, activeSheetNameRef }: PickModeDeps) {
  const {
    gridWrapRef,
    highlightTextRef,
    caretRef,
    draftRef,
    pickTargetRef,
    resizeRef,
    focusedEditorRef,
    cellElRef,
    barElRef,
    setDraft,
  } = session;

  const [pickActive, setPickActive] = useState(false);
  const cellPickRef = useRef<{ anchor: Item; last: string } | null>(null);

  const cellAtPoint = useCallback(
    (x: number, y: number, seed: Item): Item | null => {
      const grid = gridRef.current;
      if (!grid) return null;
      let [col, row] = seed;
      for (let i = 0; i < 1000; i++) {
        const b = grid.getBounds(col, row);
        if (!b) return null;
        if (x < b.x) {
          if (col === 0) return null;
          col--;
        } else if (x >= b.x + b.width) {
          col++;
        } else if (y < b.y) {
          if (row === 0) return null;
          row--;
        } else if (y >= b.y + b.height) {
          row++;
        } else {
          return [col, row];
        }
      }
      return null;
    },
    [gridRef],
  );

  const applyRef = useCallback(
    (target: { start: number; end: number }, refText: string): { start: number; end: number } | null => {
      const text = highlightTextRef.current;
      const cellTarget = draftRef.current?.cell;
      if (text === null || !cellTarget) return null;
      const applied = applyPick(text, target, refText);
      caretRef.current = applied.caret;
      setDraft({ cell: cellTarget, text: applied.text });
      return { start: target.start, end: target.start + refText.length };
    },
    [highlightTextRef, draftRef, caretRef, setDraft],
  );

  const findCornerHit = useCallback(
    (clientX: number, clientY: number) => {
      const text = highlightTextRef.current;
      const grid = gridRef.current;
      if (text === null || !grid) return null;
      for (const ref of extractFormulaRefs(text)) {
        if (ref.addr.start.sheet && !sheetNamesEqual(ref.addr.start.sheet, activeSheetNameRef.current ?? "")) continue;
        const range = rangeRect(ref.addr);
        const tl = grid.getBounds(range.x, range.y);
        const br = grid.getBounds(range.x + range.width - 1, range.y + range.height - 1);
        if (!tl || !br) continue;
        const px = { x: tl.x, y: tl.y, width: br.x + br.width - tl.x, height: br.y + br.height - tl.y };
        const corner = hitCorner(px, { x: clientX, y: clientY }, CORNER_TOLERANCE);
        if (corner) return { span: ref.span, range, corner };
      }
      return null;
    },
    [highlightTextRef, gridRef, activeSheetNameRef],
  );

  useEffect(() => {
    const el = gridWrapRef.current;
    if (!el) return;
    const beginPick = (e: PointerEvent) => {
      pickTargetRef.current = null;
      resizeRef.current = null;
      cellPickRef.current = null;
      if (e.target instanceof Element && e.target.closest("[data-cell-editor]")) return;
      const text = highlightTextRef.current;
      const draft = draftRef.current;
      if (text === null || !draft) return;
      const hit = findCornerHit(e.clientX, e.clientY);
      if (hit) {
        e.preventDefault();
        e.stopPropagation();
        cellPickRef.current = { anchor: [hit.range.x, hit.range.y], last: "" };
        resizeRef.current = {
          target: { start: hit.span.start, end: hit.span.end },
          anchor: anchorFor(hit.range, hit.corner),
        };
        setPickActive(true);
        return;
      }
      const span = pickSpanAt(text, caretRef.current);
      if (!span) return;
      const cell = cellAtPoint(e.clientX, e.clientY, draft.cell);
      if (!cell) return;
      e.preventDefault();
      e.stopPropagation();
      const refText = formatRef({ col: cell[0], row: cell[1] }, { col: cell[0], row: cell[1] });
      cellPickRef.current = { anchor: cell, last: refText };
      pickTargetRef.current = applyRef(span, refText) ?? span;
      setPickActive(true);
    };
    const swallow = (e: Event) => {
      if (!cellPickRef.current) return;
      e.preventDefault();
      e.stopPropagation();
    };
    const onPointerMove = (e: PointerEvent) => {
      const gesture = cellPickRef.current;
      if (!gesture) return;
      const cell = cellAtPoint(e.clientX, e.clientY, gesture.anchor);
      if (!cell) return;
      const resize = resizeRef.current;
      if (resize) {
        const refText = formatRef(resize.anchor, { col: cell[0], row: cell[1] });
        if (refText === gesture.last) return;
        gesture.last = refText;
        const next = applyRef(resize.target, refText);
        if (next) resize.target = next;
        return;
      }
      const target = pickTargetRef.current;
      if (!target) return;
      const [ax, ay] = gesture.anchor;
      const [cx, cy] = cell;
      const refText = formatRef(
        { col: Math.min(ax, cx), row: Math.min(ay, cy) },
        { col: Math.max(ax, cx), row: Math.max(ay, cy) },
      );
      if (refText === gesture.last) return;
      gesture.last = refText;
      const next = applyRef(target, refText);
      if (next) pickTargetRef.current = next;
    };
    const onPointerUp = () => {
      if (!pickTargetRef.current && !resizeRef.current) return;
      pickTargetRef.current = null;
      resizeRef.current = null;
      window.setTimeout(() => {
        cellPickRef.current = null;
      }, 0);
      setPickActive(false);
      const input = focusedEditorRef.current === "bar" ? barElRef.current : cellElRef.current;
      if (!input) return;
      requestAnimationFrame(() => {
        input.focus();
        input.setSelectionRange(caretRef.current, caretRef.current);
      });
    };
    el.addEventListener("pointerdown", beginPick, { capture: true });
    el.addEventListener("mousedown", swallow, { capture: true });
    el.addEventListener("mouseup", swallow, { capture: true });
    el.addEventListener("click", swallow, { capture: true });
    el.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      el.removeEventListener("pointerdown", beginPick, { capture: true });
      el.removeEventListener("mousedown", swallow, { capture: true });
      el.removeEventListener("mouseup", swallow, { capture: true });
      el.removeEventListener("click", swallow, { capture: true });
      el.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [
    gridWrapRef,
    findCornerHit,
    applyRef,
    cellAtPoint,
    highlightTextRef,
    caretRef,
    draftRef,
    pickTargetRef,
    resizeRef,
    focusedEditorRef,
    cellElRef,
    barElRef,
  ]);

  return { pickActive };
}
